import type { SceneData, SceneLoadFail } from "@novorender/data-js-api";
import type { ReadonlyVec3 } from "gl-matrix";

import { dataApi } from "apis/dataV1";
import { sceneDataFromConfig } from "apis/dataV1/sceneData";
import type { ModelTreePermission } from "apis/dataV2/authTypes";
import { Permission } from "apis/dataV2/permissions";
import type { ProjectInfo } from "apis/dataV2/projectTypes";
import { epsgDefinitionUrl } from "apis/epsg";
import { type Captured, capturedSceneOpen } from "features/offline/hostData/offlineHostData";
import { keepIfVersion, ofVersion, requestSceneOpen, type SceneOpenAnswer } from "features/offline/hostData/sceneOpen";
import { telemetry } from "telemetry";
import { isVersionComparisonSceneId, parentSceneIdFromUrl } from "utils/sceneUrl";

import type { CadCamera, SceneConfig } from "../types";
import { sceneConfigFromData, tm2LatLon } from "../utils";

/**
 * The standalone (Novorender backend) scene open's data: what it asks the server for before the
 * engine loads the scene, and the version check after. Offline, each request reads the capture
 * (`requestSceneOpen`) and the engine reads only the offline copy (`offlineOnly`). Online, the
 * server answers, however long it takes. Captured data is kept only if the engine loads its
 * version (`settle`). Otherwise the open waits for the server.
 */
export type PendingStandaloneScene = {
    /** What the engine loads the scene from. */
    readonly url: URL;
    readonly parentSceneId: string;
    readonly isVersionComparison: boolean;
    /** Whether the engine reads the scene's offline copy only, the network not at all. */
    readonly offlineOnly: true | undefined;
    /** The scene config, which may yet be replaced by the server's (see {@link settle}). */
    readonly sceneConfig: SceneConfig;
    /**
     * The scene's data, once the engine has loaded `loadedManifestId` (the scene index's
     * `offline.manifest`; `undefined` if it names none). Rejects as the requests would have.
     */
    settle(loadedManifestId: string | undefined): Promise<StandaloneSceneData>;
};

export type StandaloneSceneData = {
    readonly sceneConfig: SceneConfig;
    readonly sceneCamera: CadCamera | undefined;
    readonly projectV2: ProjectInfo | undefined;
    /** The model tree paths the user may see the scene's objects under. */
    readonly availableResourcePaths: string[];
};

/** The scene-open requests, as the viewer makes them online. */
export type StandaloneSceneRequests = {
    /** `dataApi.loadScene`: rejects only when the request does. */
    sceneConfig(): Promise<SceneData | SceneLoadFail>;
    /** RTK Query's `getProject`, unwrapped. */
    project(args: { projectId: string }): Promise<ProjectInfo>;
    /** RTK Query's `getModelTreePermissions`, unwrapped. */
    modelTreePermissions(args: { projectId: string }): Promise<ModelTreePermission[]>;
    /** Whether the viewer is online now (`selectIsOnline`). Read per request. */
    isOnline(): boolean;
};

export async function loadStandaloneScene({
    sceneId,
    hasCapture,
    requests,
}: {
    sceneId: string;
    /**
     * Whether the scene has a capture (see `capturedSceneOpen.manifestId`). Awaited only offline,
     * so online requests don't wait for the offline state.
     */
    hasCapture: Promise<boolean>;
    requests: StandaloneSceneRequests;
}): Promise<PendingStandaloneScene> {
    const fromCapture =
        <C>(read: () => Promise<C | undefined>) =>
        async (): Promise<C | undefined> =>
            (await hasCapture) ? read() : undefined;

    const sceneConfigAnswer = await requestSceneOpen<SceneData | SceneLoadFail, Captured<SceneData>>({
        isOnline: requests.isOnline(),
        network: requests.sceneConfig,
        capture: fromCapture(async () => {
            const captured = await capturedSceneOpen.sceneConfig(sceneId);
            return (
                captured && {
                    data: sceneDataFromConfig(captured.data, sceneId, requests.sceneConfig, requests.isOnline),
                    manifestId: captured.manifestId,
                }
            );
        }),
    });
    const capturedSceneData = sceneConfigAnswer.from === "capture" ? sceneConfigAnswer.captured.data : undefined;
    // An error the server answered with is thrown here, as it always was.
    const [sceneConfig, sceneCamera] = sceneConfigFromData(
        sceneConfigAnswer.from === "network" ? sceneConfigAnswer.data : sceneConfigAnswer.captured.data,
    );

    const url = new URL(sceneConfig.url);
    const parentSceneId = parentSceneIdFromUrl(url);
    const isVersionComparison = isVersionComparisonSceneId(parentSceneId);
    const projectArgs = { projectId: sceneId };
    const modelTreeArgs = { projectId: isVersionComparison ? (sceneConfig.projectId ?? sceneId) : sceneId };

    const [projectAnswer, resourcePathsAnswer] = await Promise.all([
        requestSceneOpen<ProjectInfo | null, Captured<ProjectInfo | null>>({
            isOnline: requests.isOnline(),
            network: () =>
                requests.project(projectArgs).catch((e) => {
                    if (e.status === 401 || e.status === 403) {
                        throw Object.assign(new Error("Not authorized"), {
                            error: "Not authorized",
                            statusCode: e.status,
                        });
                    }
                    throw e;
                }),
            capture: fromCapture(() => capturedSceneOpen.project(sceneId, projectArgs)),
        }),
        requestSceneOpen<string[], Captured<string[]>>({
            isOnline: requests.isOnline(),
            network: () => requests.modelTreePermissions(modelTreeArgs).then(availableResourcePaths),
            capture: fromCapture(async () => {
                const captured = await capturedSceneOpen.modelTreePermissions(sceneId, modelTreeArgs);
                // An empty answer, which fails the open online, is captured as none to answer with.
                return captured && captured.data !== null
                    ? { data: availableResourcePaths(captured.data), manifestId: captured.manifestId }
                    : undefined;
            }),
        }),
    ]);

    const offlineOnly = !requests.isOnline() && (await hasCapture) ? true : undefined;

    return {
        url,
        parentSceneId,
        isVersionComparison,
        offlineOnly,
        sceneConfig,
        async settle(loadedManifestId) {
            const [settledSceneData, projectV2, resourcePaths] = await Promise.all([
                keepIfVersion(sceneConfigAnswer, loadedManifestId),
                keepIfVersion(projectAnswer, loadedManifestId),
                keepIfVersion(resourcePathsAnswer, loadedManifestId),
            ]);
            const [settledConfig, settledCamera] =
                settledSceneData === capturedSceneData || sceneConfigAnswer.from === "network"
                    ? [sceneConfig, sceneCamera]
                    : sceneConfigFromData(settledSceneData);
            return {
                sceneConfig: settledConfig,
                sceneCamera: settledCamera,
                projectV2: projectV2 ?? undefined,
                availableResourcePaths: resourcePaths,
            };
        },
    };
}

/**
 * The open scene's config again, for the home button's reset. Online it is the server's. Offline,
 * it is the capture's of the version loaded.
 */
export async function reloadSceneConfig({
    sceneId,
    isOnline,
    loadedManifestId,
}: {
    sceneId: string;
    /** Whether the viewer is online now (`selectIsOnline`). */
    isOnline: () => boolean;
    loadedManifestId: string | undefined;
}): Promise<[SceneConfig, CadCamera | undefined]> {
    const answer = await requestSceneOpen<SceneData | SceneLoadFail, SceneData>({
        isOnline: isOnline(),
        network: () => dataApi.loadScene(sceneId),
        capture: async () => {
            const config = ofVersion(await capturedSceneOpen.sceneConfig(sceneId), loadedManifestId);
            return config && sceneDataFromConfig(config, sceneId, undefined, isOnline);
        },
    });
    return sceneConfigFromData(answer.from === "network" ? answer.data : answer.captured);
}

/** The paths the model tree permissions let the user see the scene's objects under. */
function availableResourcePaths(permissions: ModelTreePermission[]): string[] {
    return permissions
        .filter((p) => p.path && p.permissionIds?.some((p) => p === Permission.SceneView || p === Permission.Scene))
        .map((p) => p.path as string);
}

/**
 * The project's coordinate system, for coordinate conversions (`tmZoneForCalc`). Online it is
 * looked up by its EPSG code. Offline, it is read from the capture of the version loaded. A
 * capture lets a project with an EPSG code open offline.
 */
export async function loadCoordinateSystem({
    sceneId,
    projectV2,
    tmZoneV1,
    sceneCenter,
    isOnline,
    hasCapture,
    loadedManifestId,
}: {
    sceneId: string;
    projectV2: ProjectInfo | undefined;
    tmZoneV1: string | undefined;
    sceneCenter: ReadonlyVec3;
    isOnline: boolean;
    hasCapture: boolean;
    loadedManifestId: string | undefined;
}): Promise<string | undefined> {
    const epsg = projectV2?.epsg;
    const answer: SceneOpenAnswer<string | undefined, { tmZone: string | undefined }> = await requestSceneOpen({
        isOnline,
        network: () => loadTmZoneForCalc(projectV2, tmZoneV1, sceneCenter),
        capture:
            hasCapture && epsg
                ? async () => {
                      const definitions = ofVersion(
                          await capturedSceneOpen.coordinateSystem(sceneId, epsg),
                          loadedManifestId,
                      );
                      return (
                          definitions && {
                              tmZone: await loadTmZoneForCalc(
                                  projectV2,
                                  tmZoneV1,
                                  sceneCenter,
                                  async (_, format) => definitions[format] ?? undefined,
                              ),
                          }
                      );
                  }
                : undefined,
    });
    return answer.from === "network" ? answer.data : answer.captured.tmZone;
}

/** An EPSG definition's text, or `undefined` where there is none to have. */
type EpsgDefinition = (epsg: string, format: "wkt" | "proj4") => Promise<string | undefined>;

async function fetchEpsgDefinition(epsg: string, format: "wkt" | "proj4"): Promise<string | undefined> {
    const resp = await fetch(epsgDefinitionUrl(epsg, format));
    if (resp.ok) {
        return await resp.text();
    }
    console.warn(await resp.text());
    return undefined;
}

export async function loadTmZoneForCalc(
    projectV2: ProjectInfo | undefined,
    tmZoneV1: string | undefined,
    sceneCenter: ReadonlyVec3,
    definition: EpsgDefinition = fetchEpsgDefinition,
) {
    if (projectV2) {
        if (projectV2.epsg) {
            try {
                // Try to use either .wkt or .proj4
                // Some conversions don't work in WKT, probably because proj4 doesn't support newer WKT versions
                // And some conversions don't work in proj4, because they require downloading additional
                const wkt = await definition(projectV2.epsg, "wkt");
                if (wkt !== undefined) {
                    try {
                        const converted = tm2LatLon({
                            tmZone: wkt,
                            coords: sceneCenter,
                        });
                        if (!Number.isNaN(converted.latitude) && !Number.isNaN(converted.longitude)) {
                            return wkt;
                        }
                    } catch (ex) {
                        console.warn("Error using WKT string for coordinate conversion, use proj4 instead", ex);
                    }
                }

                // WKT failed, try proj4
                const proj4 = await definition(projectV2.epsg, "proj4");
                if (proj4 !== undefined) {
                    return proj4;
                }
            } catch (ex) {
                telemetry.trackCaughtException({
                    source: "render.useHandleInit.loadTmZoneForCalc",
                    category: "init",
                    error: ex,
                });
                throw ex;
            }
        }
    } else {
        return tmZoneV1;
    }
}
